import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Define the number of days after which data should be deleted
const DAYS_TO_KEEP_ORDER_HISTORY = 20;

function MyOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [trackingStatus, setTrackingStatus] = useState({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const navigate = useNavigate();

  const API_BASE_URL = 'https://bazar-wala-main-server-974485829900.asia-south1.run.app';

  // Function to translate status to English
  const getTranslatedStatus = useCallback((status) => {
    switch (status) {
      case 'Processing':
        return 'Processing';
      case 'Packaging':
        return 'Packaging';
      case 'Cancel':
        return 'Cancelled';
      case 'Return':
        return 'Returned';
      case 'Confirm':
        return 'Confirmed';
      case 'Handed over to Courier':
        return 'Handed over to Courier';
      case 'On the Way':
        return 'On the Way';
      case 'Delivered':
        return 'Delivered';
      default:
        return status;
    }
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });

    try {
      const storedData = localStorage.getItem('orderHistory');
      if (storedData) {
        const parsedOrders = JSON.parse(storedData);
        const currentTime = new Date().getTime();

        // Filter out orders older than DAYS_TO_KEEP_ORDER_HISTORY
        const recentOrders = parsedOrders.filter(order => {
          if (!order.created_at) return true;
          const orderDate = new Date(order.created_at).getTime();
          const twentyDaysInMillis = DAYS_TO_KEEP_ORDER_HISTORY * 24 * 60 * 60 * 1000;
          return (currentTime - orderDate) < twentyDaysInMillis;
        });

        // Remove duplicates from the recent orders
        const uniqueRecentOrders = [];
        const orderIds = new Set();

        recentOrders.forEach(order => {
          if (order.orderId && !orderIds.has(order.orderId)) {
            uniqueRecentOrders.push(order);
            orderIds.add(order.orderId);
          }
        });

        // Sort orders by date (latest first)
        const sortedOrders = uniqueRecentOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // Update localStorage with only the recent and unique orders
        localStorage.setItem('orderHistory', JSON.stringify(sortedOrders));

        // Set the state with translated status
        setOrders(sortedOrders.map(order => ({
          ...order,
          status: getTranslatedStatus(order.status)
        })));
      }
    } catch (error) {
      console.error("Error loading/filtering order history from localStorage:", error);
      setOrders([]);
    }
  }, [getTranslatedStatus]);

  const handleTrackOrder = async (orderId) => {
    if (!orderId) {
      setTrackingStatus(prev => ({ ...prev, [orderId]: { message: 'Order ID not found.', isError: true } }));
      return;
    }

    setTrackingStatus(prev => ({ ...prev, [orderId]: { message: 'Tracking...', isError: false, isLoading: true } }));

    try {
      const response = await fetch(`${API_BASE_URL}/localorders/orders/${orderId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const latestOrderStatus = data[0].status;
        const translatedStatus = getTranslatedStatus(latestOrderStatus);

        setTrackingStatus(prev => ({
          ...prev,
          [orderId]: { message: `Status: ${translatedStatus}`, isError: false, isLoading: false }
        }));

        // Update the order in the `orders` state with the new translated status
        setOrders(prevOrders => prevOrders.map(order =>
          order.orderId === orderId ? { ...order, status: translatedStatus } : order
        ));

      } else {
        setTrackingStatus(prev => ({ ...prev, [orderId]: { message: 'No information found for this order.', isError: true, isLoading: false } }));
      }
    } catch (error) {
      console.error("Error tracking order:", error);
      setTrackingStatus(prev => ({ ...prev, [orderId]: { message: `Tracking failed: ${error.message}`, isError: true, isLoading: false } }));
    }
  };

  const handleImageClick = (imageUrl) => {
    setCurrentImage(imageUrl);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setCurrentImage('');
  };

  return (
    <div className="bg-gray-100 min-h-screen py-6">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <div className="mb-8" data-aos="fade-down">
          <h1 className="text-3xl font-bold text-gray-900 text-center">
            My Orders
          </h1>
          <div className="mt-2 h-1 w-20 bg-amazon-orange mx-auto rounded-full"></div>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-aos="fade-up">
            <p className="text-xl text-gray-600 mb-4">
              No orders found.
            </p>
            <p className="text-gray-500">
              Return to home to place an order.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.map((order, index) => (
              <div
                key={order.orderId || index}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                data-aos="zoom-in"
                data-aos-delay={`${index * 100}`}
              >
                {/* Order Card Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-amazon-orange">
                    Order ID: {order.orderId}
                  </h3>
                </div>

                {/* Order Details */}
                <div className="px-6 py-4">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Date:</span>
                      <strong className="text-gray-900">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })
                          : 'Unknown'}
                      </strong>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Customer:</span>
                      <strong className="text-gray-900">{order.customerInfo?.name || 'Unknown'}</strong>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Phone:</span>
                      <strong className="text-gray-900">{order.customerInfo?.phone || 'Unknown'}</strong>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Current Status:</span>
                      <strong className="text-green-600">{order.status || 'Unknown'}</strong>
                    </div>
                  </div>

                  {/* Products List */}
                  {order.cartItems && order.cartItems.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Products:
                      </h4>
                      <ul className="space-y-2">
                        {order.cartItems.map((item, itemIndex) => (
                          <li key={item.productId || itemIndex} className="flex items-center gap-3">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                onClick={() => handleImageClick(item.image)}
                                className="w-12 h-12 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                              />
                            )}
                            <div className="flex-grow flex justify-between items-center text-sm">
                              <span className="text-gray-700">{item.name}</span>
                              <strong className="text-gray-900">{item.quantity} pcs</strong>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Track Order Button */}
                  <button
                    onClick={() => handleTrackOrder(order.orderId)}
                    disabled={trackingStatus[order.orderId]?.isLoading}
                    className={`w-full mt-4 py-2.5 px-4 rounded-md font-medium text-sm transition-colors ${trackingStatus[order.orderId]?.isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-amazon-yellow hover:bg-yellow-500 text-gray-900 border border-yellow-600'
                      }`}
                  >
                    {trackingStatus[order.orderId]?.isLoading ? 'Tracking...' : 'Track Order'}
                  </button>

                  {/* Tracking Status Message */}
                  {trackingStatus[order.orderId] && (
                    <p
                      className={`mt-3 text-sm font-semibold text-center ${trackingStatus[order.orderId].isError ? 'text-red-600' : 'text-green-600'
                        }`}
                    >
                      {trackingStatus[order.orderId].message}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Go Home Button */}
        <div className="text-center mt-8" data-aos="fade-up" data-aos-delay="200">
          <button
            onClick={() => navigate('/')}
            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-md"
          >
            Return to Home Page
          </button>
        </div>
      </div>

      {/* Image Popup */}
      {isPopupOpen && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 transition-opacity duration-300 ${isPopupOpen ? 'opacity-100' : 'opacity-0'
            }`}
          onClick={handlePopupClose}
        >
          <div
            className={`bg-white rounded-lg shadow-2xl max-w-[90%] max-h-[90%] flex flex-col justify-center items-center relative transform transition-transform duration-300 ${isPopupOpen ? 'scale-100' : 'scale-80'
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handlePopupClose}
              className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex justify-center items-center text-xl font-bold z-10 transition-colors"
            >
              &times;
            </button>
            {currentImage && (
              <img
                src={currentImage}
                alt="Popup"
                className="max-w-full max-h-[80vh] rounded-lg object-contain p-5"
              />
            )}
          </div>
        </div>
      )}

      {/* Custom CSS for Amazon colors */}
      <style jsx>{`
        .bg-amazon-orange {
          background-color: #ff9900;
        }
        .text-amazon-orange {
          color: #ff9900;
        }
        .bg-amazon-yellow {
          background-color: #ffd814;
        }
        .hover\\:bg-yellow-500:hover {
          background-color: #f7ca00;
        }
      `}</style>
    </div>
  );
}

export default MyOrdersPage;